import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {DrawBrushOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/draw-brush-options';
import {DrawBrushSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/draw-brush-size-option';
import {DrawColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/draw-color-option';
import {Button, Size, Type} from '@Components/button';
import {DrawPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/draw-editing-options/draw-editing-options.types';
import {DrawColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/draw-color-panel';
import {useAppSelector} from '@/hooks';

export function DrawEditingOptions(): ReactElement<any> {
  const isImageBeingGenerated = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isImageBeingGenerated;
  });
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(DrawPanels.NONE);

  const getHeaderText = (): string => {
    if (currentlyOpenedPanel === DrawPanels.COLOR) {
      return window.i18next.t('pmwjs_color');
    }
    return window.i18next.t('pmwjs_draw');
  };

  const getHeader = (): ReactElement<any> => {
    return (
      <ContextualMenuHeader id="qr-contextual-header">
        <ContextualMenuTextHeader
          text={getHeaderText()}
          showBackButton={currentlyOpenedPanel === DrawPanels.COLOR}
          onBackButton={(): void => {
            setCurrentlyOpenedPanel(DrawPanels.NONE);
          }}
        />
      </ContextualMenuHeader>
    );
  };

  const disableDrawMode = (): void => {
    void window.posterEditor?.whiteboard?.drawing.updateFromObject({
      isDrawModeOn: false,
    });
  };

  const getFooter = (): ReactElement<any> | null => {
    if (currentlyOpenedPanel === DrawPanels.COLOR) {
      return null;
    }
    return (
      <div className="spacing-p-6 _full-width _full-height">
        <Button
          type={Type.PRIMARY}
          size={Size.SMALL}
          customClasses="_full-width"
          id="draw-finish-drawing-button"
          text={window.i18next.t('pmwjs_finish_drawing')}
          icon="icon-check-circle"
          onClick={disableDrawMode}
          disabled={isImageBeingGenerated}
        />
      </div>
    );
  };

  const getDefaultOptions = (): ReactElement<any> => {
    return (
      <>
        <DrawBrushOptions />
        <DrawColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(DrawPanels.COLOR);
          }}
        />
        <DrawBrushSizeOption />
      </>
    );
  };

  const getColorPanel = (): ReactElement<any> => {
    return <DrawColorPanel />;
  };

  const getOptions = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case DrawPanels.NONE:
        return getDefaultOptions();
      case DrawPanels.COLOR:
        return getColorPanel();
      default:
        throw new Error('Unhandled panel type for draw option');
    }
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu id="qr-editing-option" footerContent={getFooter()} header={getHeader()} includeLeftRightPadding={currentlyOpenedPanel === DrawPanels.NONE}>
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentlyOpenedPanel]);
}
